import MathUtil from "@/util/MathUtil";

/**
 * ReportValidator - Client-side validation of Z-report and Turnover report data.
 *
 * Validates internal consistency of report numbers already loaded in the GUI.
 * No backend calls are made — all checks run against the in-memory data.
 *
 * Validation checks performed:
 *
 * 1. Total: Netto = Brutto - MVA
 *    Verifies that the Total row's netto value equals brutto minus MVA.
 *    This ensures the VAT calculation is consistent with the gross/net relationship.
 *
 * 2. Kategori brutto sum = Total brutto
 *    Verifies that the sum of all category brutto (amountWithVat) values equals
 *    the total brutto from the "Total omsetning" section. Categories are grouped
 *    by itemType and vatRate into rows: Mat(takeOut), Kalddrikker(takeOut),
 *    Mat(dineIn), Kalddrikker(dineIn), Alkoholdrikker(dineIn), Utkjøringer(dineIn).
 *
 * 3. Kategori MVA sum = Total MVA
 *    Verifies that the sum of individually calculated category MVA values equals
 *    the total MVA. MVA per category is calculated as: brutto / (100 + vatRate) * vatRate.
 *
 * 4. Total salg = Total brutto + Tips
 *    Verifies that the total payments (kontant + kredittkort + BankAxept + bonuspoeng)
 *    equals the total brutto sales plus tips. Card payments include legacy Vipps amounts.
 *
 * 5. Refusjon: sum beløp = Total refusjon
 *    Verifies that the sum of refund sub-items (kontant, kredittkort, BankAxept, bonus)
 *    equals the total refund amount. Only checked when classified refund data exists.
 *
 * 6. Refusjon: sum antall ≥ Returkvitteringer
 *    Verifies that every refund has at least one payment: the refund counts per type add up to at least the number
 *    of returns. A refund paid back in cash and by card is one return with two payments, as a «Kontant og kort»
 *    sale is one sale. «Total refusjon» antall is that sum, the payments, as «Total salg» antall is (founder,
 *    2026-10-29); the number of returns is «Returkvitteringer».
 *
 * Tolerance: 0.90 NOK to account for rounding differences across aggregated reports.
 *
 * Item types used in category grouping:
 *   - DISH, ADD_ON       → Mat (food)
 *   - DRINK, DRINK_BUBBLE_TEA → Kalddrikker (cold drinks)
 *   - DRINK_ALCOHOL      → Alkoholdrikker (alcohol)
 *   - DELIVERY_SERVICE   → Utkjøringer (delivery service fee)
 *
 * VAT rates from the vat config object:
 *   - vat.takeOut → applied to takeaway items (typically 15%)
 *   - vat.dineIn  → applied to dine-in items (typically 25%)
 */
class ReportValidator {

    /**
     * Validate a single z-report object.
     * Checks internal consistency of one day's z-report data.
     *
     * @param {Object} zReport - The z-report data object (cagistahospitality level)
     * @param {Object} vat - VAT config with takeOut and dineIn rates
     * @returns {Array} Array of { label, actual, expected, match } result objects
     */
    validateZReport(zReport, vat) {
        const results = [];
        const r2 = (n) => MathUtil.roundTwoDecimals(n || 0);

        // Calculate brutto per sales group from category data
        const cats = zReport.categories || [];

        const foodTakeawayBrutto = r2(cats.filter(c => (c.itemType === 'DISH' || c.itemType === 'ADD_ON') && c.vatRate === vat.takeOut).reduce((s, c) => s + c.amountWithVat, 0));
        const coldDrinksTakeawayBrutto = r2(cats.filter(c => (c.itemType === 'DRINK' || c.itemType === 'DRINK_BUBBLE_TEA') && c.vatRate === vat.takeOut).reduce((s, c) => s + c.amountWithVat, 0));
        const foodDineInBrutto = r2(cats.filter(c => (c.itemType === 'DISH' || c.itemType === 'ADD_ON') && c.vatRate === vat.dineIn).reduce((s, c) => s + c.amountWithVat, 0));
        const coldDrinksDineInBrutto = r2(cats.filter(c => (c.itemType === 'DRINK' || c.itemType === 'DRINK_BUBBLE_TEA') && c.vatRate === vat.dineIn).reduce((s, c) => s + c.amountWithVat, 0));
        const alcoholDineInBrutto = r2(cats.filter(c => c.itemType === 'DRINK_ALCOHOL' && c.vatRate === vat.dineIn).reduce((s, c) => s + c.amountWithVat, 0));
        const deliveryServiceBrutto = r2(cats.filter(c => c.itemType === 'DELIVERY_SERVICE' && c.vatRate === vat.dineIn).reduce((s, c) => s + c.amountWithVat, 0));

        const totalBrutto = r2(foodTakeawayBrutto + coldDrinksTakeawayBrutto + foodDineInBrutto + coldDrinksDineInBrutto + alcoholDineInBrutto + deliveryServiceBrutto);

        // MVA formula: brutto / (100 + vatRate) * vatRate
        const calcVat = (brutto, vatRate) => r2((brutto / (100 + vatRate)) * vatRate);

        const totalVat = r2(
            calcVat(foodTakeawayBrutto, vat.takeOut) +
            calcVat(coldDrinksTakeawayBrutto, vat.takeOut) +
            calcVat(foodDineInBrutto, vat.dineIn) +
            calcVat(coldDrinksDineInBrutto, vat.dineIn) +
            calcVat(alcoholDineInBrutto, vat.dineIn) +
            calcVat(deliveryServiceBrutto, vat.dineIn)
        );

        const totalNetto = r2(totalBrutto - totalVat);

        // Check 1: Netto = Brutto - MVA
        results.push(this.check('Total: Netto = Brutto - MVA', totalNetto, r2(totalBrutto - totalVat)));

        // Check 2: Sum of all category brutto = Total brutto
        const categoryBruttoSum = r2(cats.reduce((s, c) => s + c.amountWithVat, 0));
        results.push(this.check('Kategori brutto sum = Total brutto', categoryBruttoSum, totalBrutto));

        // Check 3: Sum of all category MVA = Total MVA
        const categoryVatSum = r2(cats.reduce((s, c) => s + calcVat(c.amountWithVat, c.vatRate), 0));
        results.push(this.check('Kategori MVA sum = Total MVA', categoryVatSum, totalVat));

        // Check 4: Total payments = Total brutto + Tips
        const cashAmount = (zReport.cashPickupPaymentsTotalAmount || 0) + (zReport.cashDineInPaymentsTotalAmount || 0) + (zReport.cashDeliveryPaymentsTotalAmount || 0);
        const cardAmount = (zReport.cardPickupPaymentsTotalAmount || 0) + (zReport.cardDineInPaymentsTotalAmount || 0) + (zReport.cardDeliveryPaymentsTotalAmount || 0)
            + (zReport.vippsPickupPaymentsTotalAmount || 0) + (zReport.vippsDineInPaymentsTotalAmount || 0) + (zReport.vippsDeliveryPaymentsTotalAmount || 0) + (zReport.vippsDeliveryServicePaymentsTotalAmount || 0);
        const bankAxeptAmount = (zReport.bankAxeptPickupPaymentsTotalAmount || 0) + (zReport.bankAxeptDineInPaymentsTotalAmount || 0) + (zReport.bankAxeptDeliveryPaymentsTotalAmount || 0);
        const loyaltyAmount = zReport.loyaltyPointsPaymentsTotalAmount || 0;
        const totalPayments = r2(cashAmount + cardAmount + bankAxeptAmount + loyaltyAmount);

        const tips = zReport.tips || 0;
        results.push(this.check('Total salg beløp = Total brutto + Tips', totalPayments, r2(totalBrutto + tips)));

        // Check 5: Payment counts - sum of sub-counts = Total salg antall
        const cashCount = (zReport.cashPickupCount || 0) + (zReport.cashDineInCount || 0) + (zReport.cashDeliveryCount || 0);
        const cardCount = (zReport.cardPickupCount || 0) + (zReport.cardDineInCount || 0) + (zReport.cardDeliveryCount || 0)
            + (zReport.vippsPickupCount || 0) + (zReport.vippsDineInCount || 0) + (zReport.vippsDeliveryCount || 0) + (zReport.vippsDeliveryServiceCount || 0);
        const bankAxeptCount = (zReport.bankAxeptPickupCount || 0) + (zReport.bankAxeptDineInCount || 0) + (zReport.bankAxeptDeliveryCount || 0);
        const loyaltyCount = zReport.loyaltyPointsCount || 0;
        const totalPaymentCount = cashCount + cardCount + bankAxeptCount + loyaltyCount;
        results.push(this.checkInt('Total salg antall = sum antall', totalPaymentCount, cashCount + cardCount + bankAxeptCount + loyaltyCount));

        // Check 6: Payment beløp - sum of sub-amounts = Total salg beløp
        results.push(this.check('Total salg beløp = sum beløp', totalPayments, r2(cashAmount + cardAmount + bankAxeptAmount + loyaltyAmount)));

        // Check 7: Refund sub-items sum = Total refund (only when classified data exists)
        const refundCash = zReport.refundCashTotalAmount || 0;
        const refundCard = zReport.refundCardTotalAmount || 0;
        const refundBankAxept = zReport.refundBankAxeptTotalAmount || 0;
        const refundBonus = zReport.refundBonusTotalAmount || 0;
        const refundTotal = zReport.refundReceiptsTotalAmount || 0;

        const hasClassified = zReport.refundCardCount !== undefined || zReport.refundCashCount !== undefined;
        if (hasClassified && refundTotal > 0) {
            const refundSubSum = r2(refundCash + refundCard + refundBankAxept + refundBonus);
            results.push(this.check('Refusjon: sum beløp = Total refusjon', refundSubSum, r2(refundTotal)));
        }

        // Check 8: Refund counts - the payments paid back are at least the returns (Returkvitteringer)
        const refundTotalCount = zReport.refundReceiptsCount || 0;
        if (refundTotalCount > 0) {
            const refundCashCount = zReport.refundCashCount || 0;
            const refundCardCount = zReport.refundCardCount || 0;
            const refundBankAxeptCount = zReport.refundBankAxeptCount || 0;
            const refundBonusCount = zReport.refundBonusCount || 0;
            const refundCountSum = refundCashCount + refundCardCount + refundBankAxeptCount + refundBonusCount;
            results.push(this.checkAtLeast('Refusjon: sum antall ≥ Returkvitteringer', refundCountSum, refundTotalCount));
        }

        return results;
    }

    /**
     * Validate turnover report (aggregated data from multiple z-reports).
     * Same checks as single z-report but aggregated across all z-reports in the period.
     *
     * @param {Array} zReports - Array of z-report wrapper objects (each has .cagistahospitality)
     * @param {Object} vat - VAT config with takeOut and dineIn rates
     * @returns {Array} Array of { label, actual, expected, match } result objects
     */
    validateTurnoverReport(zReports, vat) {
        const results = [];
        const r2 = (n) => MathUtil.roundTwoDecimals(n || 0);
        const calcVat = (brutto, vatRate) => r2((brutto / (100 + vatRate)) * vatRate);

        // Aggregate values across all z-reports
        let foodTakeawayBrutto = 0, coldDrinksTakeawayBrutto = 0, foodDineInBrutto = 0;
        let coldDrinksDineInBrutto = 0, alcoholDineInBrutto = 0, deliveryServiceBrutto = 0;
        let totalTips = 0;
        let totalCash = 0, totalCard = 0, totalBankAxept = 0, totalLoyalty = 0;
        let totalCashCount = 0, totalCardCount = 0, totalBankAxeptCount = 0, totalLoyaltyCount = 0;
        let totalRefund = 0, refundCash = 0, refundCard = 0, refundBankAxept = 0, refundBonus = 0;
        let totalRefundCount = 0, refundCashCount = 0, refundCardCount = 0, refundBankAxeptCount = 0, refundBonusCount = 0;
        let categoryBruttoSum = 0;
        let hasClassifiedRefund = false;

        for (let x = 0; x < zReports.length; x++) {
            const zr = zReports[x].cagistahospitality;
            const cats = zr.categories || [];

            // Accumulate category amounts into sales groups
            for (const c of cats) {
                categoryBruttoSum += c.amountWithVat;
                if ((c.itemType === 'DISH' || c.itemType === 'ADD_ON') && c.vatRate === vat.takeOut) foodTakeawayBrutto += c.amountWithVat;
                else if ((c.itemType === 'DRINK' || c.itemType === 'DRINK_BUBBLE_TEA') && c.vatRate === vat.takeOut) coldDrinksTakeawayBrutto += c.amountWithVat;
                else if ((c.itemType === 'DISH' || c.itemType === 'ADD_ON') && c.vatRate === vat.dineIn) foodDineInBrutto += c.amountWithVat;
                else if ((c.itemType === 'DRINK' || c.itemType === 'DRINK_BUBBLE_TEA') && c.vatRate === vat.dineIn) coldDrinksDineInBrutto += c.amountWithVat;
                else if (c.itemType === 'DRINK_ALCOHOL' && c.vatRate === vat.dineIn) alcoholDineInBrutto += c.amountWithVat;
                else if (c.itemType === 'DELIVERY_SERVICE' && c.vatRate === vat.dineIn) deliveryServiceBrutto += c.amountWithVat;
            }

            totalTips += zr.tips || 0;

            // Accumulate payment amounts (card includes legacy Vipps)
            totalCash += (zr.cashPickupPaymentsTotalAmount || 0) + (zr.cashDineInPaymentsTotalAmount || 0) + (zr.cashDeliveryPaymentsTotalAmount || 0);
            totalCard += (zr.cardPickupPaymentsTotalAmount || 0) + (zr.cardDineInPaymentsTotalAmount || 0) + (zr.cardDeliveryPaymentsTotalAmount || 0)
                + (zr.vippsPickupPaymentsTotalAmount || 0) + (zr.vippsDineInPaymentsTotalAmount || 0) + (zr.vippsDeliveryPaymentsTotalAmount || 0) + (zr.vippsDeliveryServicePaymentsTotalAmount || 0);
            totalBankAxept += (zr.bankAxeptPickupPaymentsTotalAmount || 0) + (zr.bankAxeptDineInPaymentsTotalAmount || 0) + (zr.bankAxeptDeliveryPaymentsTotalAmount || 0);
            totalLoyalty += zr.loyaltyPointsPaymentsTotalAmount || 0;

            // Accumulate payment counts
            totalCashCount += (zr.cashPickupCount || 0) + (zr.cashDineInCount || 0) + (zr.cashDeliveryCount || 0);
            totalCardCount += (zr.cardPickupCount || 0) + (zr.cardDineInCount || 0) + (zr.cardDeliveryCount || 0)
                + (zr.vippsPickupCount || 0) + (zr.vippsDineInCount || 0) + (zr.vippsDeliveryCount || 0) + (zr.vippsDeliveryServiceCount || 0);
            totalBankAxeptCount += (zr.bankAxeptPickupCount || 0) + (zr.bankAxeptDineInCount || 0) + (zr.bankAxeptDeliveryCount || 0);
            totalLoyaltyCount += zr.loyaltyPointsCount || 0;

            // Accumulate refund amounts
            totalRefund += zr.refundReceiptsTotalAmount || 0;
            refundCash += zr.refundCashTotalAmount || 0;
            refundCard += zr.refundCardTotalAmount || 0;
            refundBankAxept += zr.refundBankAxeptTotalAmount || 0;
            refundBonus += zr.refundBonusTotalAmount || 0;

            if (zr.refundCardCount !== undefined || zr.refundCashCount !== undefined) hasClassifiedRefund = true;

            // Accumulate refund counts
            totalRefundCount += zr.refundReceiptsCount || 0;
            refundCashCount += zr.refundCashCount || 0;
            refundCardCount += zr.refundCardCount || 0;
            refundBankAxeptCount += zr.refundBankAxeptCount || 0;
            refundBonusCount += zr.refundBonusCount || 0;
        }

        const totalBrutto = r2(foodTakeawayBrutto + coldDrinksTakeawayBrutto + foodDineInBrutto + coldDrinksDineInBrutto + alcoholDineInBrutto + deliveryServiceBrutto);
        const totalVat = r2(
            calcVat(foodTakeawayBrutto, vat.takeOut) +
            calcVat(coldDrinksTakeawayBrutto, vat.takeOut) +
            calcVat(foodDineInBrutto, vat.dineIn) +
            calcVat(coldDrinksDineInBrutto, vat.dineIn) +
            calcVat(alcoholDineInBrutto, vat.dineIn) +
            calcVat(deliveryServiceBrutto, vat.dineIn)
        );
        const totalNetto = r2(totalBrutto - totalVat);

        // Check 1: Netto = Brutto - MVA
        results.push(this.check('Total: Netto = Brutto - MVA', totalNetto, r2(totalBrutto - totalVat)));

        // Check 2: Category brutto sum = Total brutto
        results.push(this.check('Kategori brutto sum = Total brutto', r2(categoryBruttoSum), totalBrutto));

        // Check 3: Category MVA sum = Total MVA
        const categoryVatSum = r2(zReports.reduce((s, zr) => {
            return s + (zr.cagistahospitality.categories || []).reduce((ss, c) => ss + calcVat(c.amountWithVat, c.vatRate), 0);
        }, 0));
        results.push(this.check('Kategori MVA sum = Total MVA', categoryVatSum, totalVat));

        // Check 4: Total payments beløp = Total brutto + Tips
        const totalPayments = r2(totalCash + totalCard + totalBankAxept + totalLoyalty);
        results.push(this.check('Total salg beløp = Total brutto + Tips', totalPayments, r2(totalBrutto + totalTips)));

        // Check 5: Payment counts - sum = Total salg antall
        const totalPaymentCount = totalCashCount + totalCardCount + totalBankAxeptCount + totalLoyaltyCount;
        results.push(this.checkInt('Total salg antall = sum antall', totalPaymentCount, totalCashCount + totalCardCount + totalBankAxeptCount + totalLoyaltyCount));

        // Check 6: Payment beløp - sum = Total salg beløp
        results.push(this.check('Total salg beløp = sum beløp', totalPayments, r2(totalCash + totalCard + totalBankAxept + totalLoyalty)));

        // Check 7: Refund sub-items beløp = Total refund
        if (hasClassifiedRefund && totalRefund > 0) {
            results.push(this.check('Refusjon: sum beløp = Total refusjon', r2(refundCash + refundCard + refundBankAxept + refundBonus), r2(totalRefund)));
        }

        // Check 8: Refund counts - the payments paid back are at least the returns (Returkvitteringer)
        if (totalRefundCount > 0) {
            const refundCountSum = refundCashCount + refundCardCount + refundBankAxeptCount + refundBonusCount;
            results.push(this.checkAtLeast('Refusjon: sum antall ≥ Returkvitteringer', refundCountSum, totalRefundCount));
        }

        return results;
    }

    /**
     * Compare actual vs expected values with a tolerance of 0.02 for floating point rounding.
     *
     * @param {string} label - Description of the check
     * @param {number} actual - Computed actual value
     * @param {number} expected - Expected value
     * @returns {Object} { label, actual (formatted), expected (formatted), match (boolean) }
     */
    checkAtLeast(label, actual, expected) {
        return {
            label,
            actual: String(actual),
            expected: '≥ ' + String(expected),
            match: actual >= expected
        };
    }

    checkInt(label, actual, expected) {
        const match = actual === expected;
        return {
            label,
            actual: String(actual),
            expected: String(expected),
            match
        };
    }

    check(label, actual, expected) {
        const match = Math.abs(actual - expected) < 0.9;
        return {
            label,
            actual: MathUtil.displayTwoDecimals(actual),
            expected: MathUtil.displayTwoDecimals(expected),
            match
        };
    }
}

export default new ReportValidator();
